import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import Tooltip from '@mui/material/Tooltip';
import TrendingUpOutlinedIcon from '@mui/icons-material/TrendingUpOutlined';
import TrendingDownOutlinedIcon from '@mui/icons-material/TrendingDownOutlined';
import PercentOutlinedIcon from '@mui/icons-material/PercentOutlined';
import PrecisionManufacturingOutlinedIcon from '@mui/icons-material/PrecisionManufacturingOutlined';
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
import CompareArrowsOutlinedIcon from '@mui/icons-material/CompareArrowsOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import styles from './DocumentKpiCards.module.scss';

const KPI_META = {
    TaxableIncome: { tone: 'income', Icon: TrendingUpOutlinedIcon },
    ExemptIncome: { tone: 'income', Icon: ReceiptLongOutlinedIcon },
    VATTransactions: { tone: 'vat', Icon: PercentOutlinedIcon },
    VATExpenses: { tone: 'expense', Icon: TrendingDownOutlinedIcon },
    VATLevies: { tone: 'expense', Icon: PercentOutlinedIcon },
    VATEquipment: { tone: 'equipment', Icon: PrecisionManufacturingOutlinedIcon },
    TotalVAT: { tone: 'total', Icon: AccountBalanceOutlinedIcon },
    ExpenseAmount: { tone: 'expense', Icon: TrendingDownOutlinedIcon },
    Total: { tone: 'total', Icon: AccountBalanceOutlinedIcon },
    acceptedWithholdingTax: { tone: 'income', Icon: ReceiptLongOutlinedIcon },
    paymentWithholdingTax: { tone: 'expense', Icon: TrendingDownOutlinedIcon },
    Difference: { tone: 'diff', Icon: CompareArrowsOutlinedIcon },
    TotalCreditAccount: { tone: 'income', Icon: TrendingUpOutlinedIcon },
    TotalDebitAccount: { tone: 'expense', Icon: TrendingDownOutlinedIcon },
};

const isEmptyValue = (value) => value == null || value === '';

const isZeroish = (value) => {
    const normalized = String(value).trim().replace(/[(),\s₪]/g, '');
    return normalized === '0' || normalized === '0.00' || normalized === '-0' || normalized === '-0.00';
};

const rowToItems = (headers = [], row) => {
    if (!row) return [];
    return Object.entries(row)
        .map(([key, value], index) => ({
            key,
            label: headers[index] || key,
            value,
        }))
        .filter((item) => item.key !== 'Source' && !isEmptyValue(item.value));
};

const KPI_ORDER_EXPENSES = {
    VATExpenses: 0,
    ExpenseAmount: 0,
    VATLevies: 1,
    VATEquipment: 2,
    EquipmentVAT: 2,
    VATTransactions: 3,
    TotalVAT: 4,
    Total: 2,
    acceptedWithholdingTax: 1,
    paymentWithholdingTax: 0,
    TotalDebitAccount: 0,
    TotalCreditAccount: 1,
    Difference: 2,
};

/** Expenses + IncludeOppositeVatInTable */
const KPI_ORDER_EXPENSES_OPPOSITE = {
    VATExpenses: 0,
    ExpenseAmount: 0,
    VATLevies: 1,
    VATEquipment: 2,
    EquipmentVAT: 2,
    VATTransactions: 3,
    TotalVAT: 4,
    TotalDebitAccount: 0,
    TotalCreditAccount: 1,
    Difference: 2,
};

const KPI_ORDER_INCOMES = {
    TaxableIncome: 0,
    ExemptIncome: 1,
    VATTransactions: 2,
    VATLevies: 3,
    TotalVAT: 4,
    Total: 2,
    acceptedWithholdingTax: 1,
    paymentWithholdingTax: 0,
    TotalDebitAccount: 0,
    TotalCreditAccount: 1,
    Difference: 2,
};

/** Incomes + IncludeOppositeVatInTable */
const KPI_ORDER_INCOMES_OPPOSITE = {
    TaxableIncome: 0,
    ExemptIncome: 1,
    VATTransactions: 2,
    VATEquipment: 3,
    EquipmentVAT: 3,
    VATExpenses: 4,
    VATLevies: 5,
    TotalVAT: 6,
    TotalDebitAccount: 0,
    TotalCreditAccount: 1,
    Difference: 2,
};

const resolveVatOrder = (documentType, oppositeVat) => {
    const isExpenses = documentType === 'Expenses';
    if (isExpenses) {
        return oppositeVat ? KPI_ORDER_EXPENSES_OPPOSITE : KPI_ORDER_EXPENSES;
    }
    return oppositeVat ? KPI_ORDER_INCOMES_OPPOSITE : KPI_ORDER_INCOMES;
};

const sortKpiItems = (items, { documentType = 'Incomes', oppositeVat = false } = {}) => {
    const order = resolveVatOrder(documentType, oppositeVat);
    return [...items].sort((a, b) => {
        const diff = (order[a.key] ?? 50) - (order[b.key] ?? 50);
        if (diff !== 0) return diff;
        return String(a.key).localeCompare(String(b.key));
    });
};

const mergeVatItems = (headers, rows, options = {}) => {
    const [totalRow, ...rest] = rows || [];
    const sourceRows = rest.filter((row) => row?.Source);
    const items = rowToItems(headers, totalRow).map((item) => ({
        ...item,
        breakdown: sourceRows
            .map((row) => ({ source: row.Source, value: row[item.key] }))
            .filter((line) => !isEmptyValue(line.value)),
    }));
    return sortKpiItems(items, options);
};

const KpiCard = ({ item, alert, direction }) => {
    const meta = KPI_META[item.key] || { tone: 'default', Icon: AccountBalanceOutlinedIcon };
    const Icon = meta.Icon;
    const breakdown = item.breakdown || [];

    return (
        <article
            className={[
                styles.card,
                styles[meta.tone] || '',
                alert ? styles.alert : '',
                breakdown.length ? styles.hasBreakdown : '',
            ].filter(Boolean).join(' ')}
            dir={direction}
        >
            <span className={styles.icon} aria-hidden="true">
                <Icon fontSize="inherit" />
            </span>
            <div className={styles.copy}>
                <span className={styles.label}>{item.label}</span>
                {breakdown.length > 0 && (
                    <ul className={styles.breakdown}>
                        {breakdown.map((line) => (
                            <li key={line.source} className={styles.breakdownRow}>
                                <Tooltip title={String(line.source ?? '')} placement="top" arrow>
                                    <span className={styles.breakdownSource}>{line.source}</span>
                                </Tooltip>
                                <Tooltip title={String(line.value ?? '')} placement="top" arrow>
                                    <span className={styles.breakdownValue}>{line.value}</span>
                                </Tooltip>
                            </li>
                        ))}
                    </ul>
                )}
                <strong className={styles.value}>{item.value}</strong>
            </div>
        </article>
    );
};

KpiCard.propTypes = {
    item: PropTypes.shape({
        key: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired,
        value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        breakdown: PropTypes.arrayOf(PropTypes.shape({
            source: PropTypes.string,
            value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        })),
    }).isRequired,
    alert: PropTypes.bool,
    direction: PropTypes.oneOf(['rtl', 'ltr']),
};

const DocumentKpiCards = ({
    direction = 'rtl',
    documentType = 'Incomes',
    vatHeaders = [],
    vatRows = [],
    ledgerHeaders = [],
    ledgerRows = [],
    includeOppositeVat = false,
    children,
    stretch = false,
}) => {
    const { t } = useTranslation();
    const sortOptions = useMemo(
        () => ({ documentType, oppositeVat: includeOppositeVat }),
        [documentType, includeOppositeVat],
    );
    const vatItems = useMemo(
        () => mergeVatItems(vatHeaders, vatRows, sortOptions),
        [vatHeaders, vatRows, sortOptions],
    );
    const ledgerItems = useMemo(
        () => sortKpiItems(rowToItems(ledgerHeaders, ledgerRows[0]), sortOptions),
        [ledgerHeaders, ledgerRows, sortOptions],
    );

    if (!vatItems.length && !ledgerItems.length) return null;

    return (
        <div
            className={[
                styles.sections,
                !vatItems.length ? styles.ledgerOnly : '',
                stretch ? styles.stretch : '',
            ].filter(Boolean).join(' ')}
            dir={direction}
        >
            {vatItems.length > 0 && (
                <section className={styles.panel}>
                    <header className={styles.panelHead}>
                        <h3 className={styles.panelTitle}>{t('Total')}</h3>
                        {children}
                    </header>
                    <div className={styles.grid} dir={direction}>
                        {vatItems.map((item) => (
                            <KpiCard key={`vat-${item.key}`} item={item} direction={direction} />
                        ))}
                    </div>
                </section>
            )}
            {ledgerItems.length > 0 && (
                <section className={`${styles.panel} ${styles.ledgerPanel}`}>
                    <svg
                        className={styles.cornerMark}
                        xmlns="http://www.w3.org/2000/svg"
                        width="32"
                        height="44"
                        viewBox="0 0 40 56"
                        fill="none"
                        aria-hidden="true"
                    >
                        <path
                            d="M0.499987 -1.52441L60.2963 -1.52441V55.8625C60.2963 55.8625 33.4225 58.71 27.0031 28.8118C20.5837 -1.08635 0.499987 -1.52441 0.499987 -1.52441Z"
                            fill="#304fff"
                        />
                    </svg>
                    <header className={styles.panelHead}>
                        <h3 className={styles.panelTitle}>
                            {t('Debit Account')} / {t('Credit Account')}
                        </h3>
                    </header>
                    <div className={`${styles.grid} ${styles.ledgerGrid}`} dir={direction}>
                        {ledgerItems.map((item) => (
                            <KpiCard
                                key={`ledger-${item.key}`}
                                item={item}
                                direction={direction}
                                alert={item.key === 'Difference' && !isZeroish(item.value)}
                            />
                        ))}
                    </div>
                </section>
            )}
        </div>
    );
};

DocumentKpiCards.propTypes = {
    direction: PropTypes.oneOf(['rtl', 'ltr']),
    documentType: PropTypes.oneOf(['Incomes', 'Expenses']),
    vatHeaders: PropTypes.arrayOf(PropTypes.string),
    vatRows: PropTypes.arrayOf(PropTypes.object),
    ledgerHeaders: PropTypes.arrayOf(PropTypes.string),
    ledgerRows: PropTypes.arrayOf(PropTypes.object),
    includeOppositeVat: PropTypes.bool,
    children: PropTypes.node,
    stretch: PropTypes.bool,
};

export default DocumentKpiCards;
